import { QueryClient, QueryKey, useMutation, useQueryClient } from "@tanstack/react-query";
import { v4 as newGuid } from "uuid";
import { configuration } from "../../../configuration";
import { MeetingAgendaItemType } from "../../../constants";
import { CreateOneOnOneMeetingDto, FetchError, GetOneOnOneMeetingsDto, OneOnOneMeetingDto, UserDto } from "../../types";
import { useAccessToken } from "../../use-access-token";
import { queryKeys } from "../queryKeys";

type Variables = {
  meetingGuid: string;
  leadUser: UserDto;
  otherUser: UserDto;
};

const useCreateOneOnOneMeetingRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createOneOnOneMeetingRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: CreateOneOnOneMeetingDto = {
      meetingGuid: variables.meetingGuid,
      leadUserId: variables.leadUser.userId,
      otherUserId: variables.otherUser.userId,
      agendaItems: [
        {
          guid: newGuid(),
          name: "Check In",
          description:
            "Start with:<br />“What is on your mind today?” or <br />“What are you noticing?” or <br />“What has been most challenging for you lately?”",
          type: MeetingAgendaItemType.Blank,
          allocatedTimeInMinutes: 0,
          sortOrder: "0|hzzzzz:",
        },
        {
          guid: newGuid(),
          name: "Strategy",
          description: "Owned strategy items are reviewed. New tasks or loops are created as needed.",
          type: MeetingAgendaItemType.UserStrategicOwnership,
          allocatedTimeInMinutes: 0,
          sortOrder: "0|i00007:",
        },
        {
          guid: newGuid(),
          name: "Tasks",
          description:
            "Personal tasks are reviewed with attention to focused tasks and how they are aligned with the overall priorities of the team. New tasks are created as needed.",
          type: MeetingAgendaItemType.UserTasks,
          allocatedTimeInMinutes: 0,
          sortOrder: "0|i0000f:",
        },
        {
          guid: newGuid(),
          name: "Feedback",
          description: "Feedback is reviewed and processed together.",
          type: MeetingAgendaItemType.UserFeedback,
          allocatedTimeInMinutes: 0,
          sortOrder: "0|i0000j:",
        },
        {
          guid: newGuid(),
          name: "Roles",
          description:
            "Roles and accountabilities are reviewed. Growth plans are discussed. New tasks or loops are created as needed.",
          type: MeetingAgendaItemType.UserRoles,
          allocatedTimeInMinutes: 0,
          sortOrder: "0|i0000n:",
        },
        {
          guid: newGuid(),
          name: "Loops",
          description: "Loops are reviewed and processed together.",
          type: MeetingAgendaItemType.UserLoops,
          allocatedTimeInMinutes: 0,
          sortOrder: "0|i0000s:",
        },
        {
          guid: newGuid(),
          name: "Check Out",
          description:
            "Depending upon the time remaining, check if there is anything else that needs a conversation. Capture topics or reminders for next time in your private Meeting Notes.",
          type: MeetingAgendaItemType.Blank,
          allocatedTimeInMinutes: 0,
          sortOrder: "0|i0000v:",
        },
      ],
    };

    const response = await fetch(`${configuration.apiRootUrl}/meetings/one-on-ones`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { createOneOnOneMeetingRequest };
};

const useCreateOneOnOneMeeting = () => {
  const { createOneOnOneMeetingRequest } = useCreateOneOnOneMeetingRequest();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createOneOnOneMeetingRequest,
    onMutate: async (variables) => {
      await queryClient.cancelQueries(queryKeys.meetings.getOneOnOneMeetingsForUser(variables.leadUser.userId));
      await queryClient.cancelQueries(queryKeys.meetings.getOneOnOneMeetingsForUser(variables.otherUser.userId));

      const newMeeting: OneOnOneMeetingDto = {
        guid: variables.meetingGuid,
        leadUser: variables.leadUser,
        otherUser: variables.otherUser,
        currentInstance: null,
        instancesCount: 0,
        lastInstanceDateTimeUtc: null,
      };

      const cachedLeadUserData = optimisticallyUpdateOneOnOneMeetingData(
        queryClient,
        queryKeys.meetings.getOneOnOneMeetingsForUser(variables.leadUser.userId),
        newMeeting,
      );

      const cachedOtherUserData = optimisticallyUpdateOneOnOneMeetingData(
        queryClient,
        queryKeys.meetings.getOneOnOneMeetingsForUser(variables.otherUser.userId),
        newMeeting,
      );

      return { cachedLeadUserData, cachedOtherUserData };
    },
    onError: (_error, variables, context) => {
      queryClient.setQueryData(
        queryKeys.meetings.getOneOnOneMeetingsForUser(variables.leadUser.userId),
        context?.cachedLeadUserData,
      );
      queryClient.setQueryData(
        queryKeys.meetings.getOneOnOneMeetingsForUser(variables.otherUser.userId),
        context?.cachedOtherUserData,
      );
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.meetings.getOneOnOneMeetingsForUser(variables.leadUser.userId));
      queryClient.invalidateQueries(queryKeys.meetings.getOneOnOneMeetingsForUser(variables.otherUser.userId));
    },
  });
};

async function optimisticallyUpdateOneOnOneMeetingData(
  queryClient: QueryClient,
  queryKey: QueryKey,
  newOneOnOneMeeting: OneOnOneMeetingDto,
) {
  await queryClient.cancelQueries(queryKey);
  const cachedData = queryClient.getQueryData<GetOneOnOneMeetingsDto>(queryKey);

  if (cachedData) {
    const optimisticallyUpdatedData: GetOneOnOneMeetingsDto = {
      ...cachedData,
      meetings: [newOneOnOneMeeting, ...cachedData.meetings],
    };

    queryClient.setQueryData(queryKey, optimisticallyUpdatedData);
    return cachedData;
  }
}

export { useCreateOneOnOneMeeting };
